import { Link, useLocation } from "react-router-dom";
import { useApp } from "@/app/app-context";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

export function NavMain() {
  const { navItems } = useApp();
  const { state } = useSidebar();
  const pathname = useLocation().pathname;

  /**
   * Determines if a navigation item is active based on the current URL path
   *
   * @param itemUrl - The URL of the navigation item
   * @returns boolean - Whether the item should be considered active
   */
  const isItemActive = (itemUrl: string): boolean => {
    // Direct match - the current path exactly matches the nav item URL
    if (pathname === itemUrl) {
      return true;
    }

    // Home/index routes need special handling
    // If this is the app's main route (like /cleanup or /get-ready),
    // only mark it active if we're exactly on that route or just one level deeper
    // This prevents the home nav item from being highlighted for all sub-routes
    if (itemUrl.split("/").length === 2 && itemUrl !== "/") {
      // For app root URLs like "/cleanup" or "/get-ready"
      const pathParts = pathname.split("/").filter(Boolean);
      const itemParts = itemUrl.split("/").filter(Boolean);

      // Return true if on exact path or just one level deeper
      return pathParts[0] === itemParts[0] && pathParts.length <= 1;
    }

    // For any other nav items, detect if the current path starts with the item URL
    // AND the next character is either nothing or a slash
    // This prevents "/cleanup/analytics" from matching both "/cleanup" and "/cleanup/a"
    if (
      pathname.startsWith(itemUrl) &&
      (pathname.length === itemUrl.length || pathname[itemUrl.length] === "/")
    ) {
      return true;
    }

    return false;
  };

  return (
    <SidebarGroup>
      <SidebarMenu>
        {navItems.map((item) => (
          <SidebarMenuItem key={item.title} className="flex justify-center">
            <SidebarMenuButton asChild>
              <Link
                to={item.url}
                className={cn(
                  "flex hover:bg-[#F1F5F9]",
                  state === "collapsed" && "justify-center",
                  isItemActive(item.url) && "bg-[#F1F5F9]"
                )}
              >
                <item.icon
                  style={{ width: "1.35rem", height: "1.35rem" }}
                  className={cn(isItemActive(item.url) && "text-[#0942A8]")}
                />
                {state === "expanded" && (
                  <span
                    className={cn(isItemActive(item.url) && "text-[#0942A8]")}
                  >
                    {item.title}
                  </span>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
